import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { useCSRF } from './hooks/useCSRF';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProfileProvider, useProfile } from './contexts/ProfileContext';
import { SUBSCRIPTION_CONFIG } from './config';

// Code splitting: Lazy load all route components
const LandingPage = React.lazy(() => import('./components/LandingPage'));
const Signup = React.lazy(() => import('./components/Signup'));
const Login = React.lazy(() => import('./components/Login'));
const ForgotPassword = React.lazy(() => import('./components/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const Verify = React.lazy(() => import('./components/Verify'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));
const RefundPolicy = React.lazy(() => import('./components/RefundPolicy'));
const CustomerDocs = React.lazy(() => import('./components/CustomerDocs'));
const Profile = React.lazy(() => import('./components/Profile'));
const OrganizationManagement = React.lazy(() => import('./components/OrganizationManagement'));
const WelcomeInvitation = React.lazy(() => import('./components/WelcomeInvitation'));
const Upgrade = React.lazy(() => import('./components/Upgrade'));
const TrialExpired = React.lazy(() => import('./components/TrialExpired'));
const Admin = React.lazy(() => import('./components/Admin'));
const PaymentConfirmation = React.lazy(() => import('./components/PaymentConfirmation'));
const InvoiceCancel = React.lazy(() => import('./components/InvoiceCancel'));
const AuditLogs = React.lazy(() => import('./components/AuditLogs'));
const TrialExpiredRedirect = React.lazy(() => import('./components/TrialExpiredRedirect'));
const NotFound = React.lazy(() => import('./components/NotFound'));
const Tickets = React.lazy(() => import('./components/Tickets'));
const CreateTicket = React.lazy(() => import('./components/CreateTicket'));
const TicketDetail = React.lazy(() => import('./components/TicketDetail'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    minHeight: '100vh', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center' 
  }}>
    Loading...
  </div>
);

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useProfile();
  
  if (loading || isAuthenticated === null) {
    // Still checking authentication
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function App() {
  // Fetch CSRF token on app load
  useCSRF();
  
  return (
    <ErrorBoundary>
      <ProfileProvider>
        <ThemeProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/verify/:token" element={<Verify />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/refund" element={<RefundPolicy />} />
                <Route path="/docs" element={<CustomerDocs />} />
                <Route path="/dashboard" element={<PrivateRoute><TrialExpiredRedirect><Dashboard /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><TrialExpiredRedirect><Profile /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/organization" element={<PrivateRoute><TrialExpiredRedirect><OrganizationManagement /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/upgrade" element={<PrivateRoute><Upgrade subscriptionDays={SUBSCRIPTION_CONFIG.DEFAULT_SUBSCRIPTION_DAYS} /></PrivateRoute>} />
                <Route path="/trial-expired" element={<PrivateRoute><TrialExpired /></PrivateRoute>} />
                <Route path="/admin" element={<PrivateRoute><TrialExpiredRedirect><Admin /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/audit-logs" element={<PrivateRoute><TrialExpiredRedirect><AuditLogs /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/tickets" element={<PrivateRoute><TrialExpiredRedirect><Tickets /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/tickets/new" element={<PrivateRoute><TrialExpiredRedirect><CreateTicket /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/tickets/:id" element={<PrivateRoute><TrialExpiredRedirect><TicketDetail /></TrialExpiredRedirect></PrivateRoute>} />
                <Route path="/invoice" element={<Navigate to="/dashboard" replace />} />
                <Route path="/invoice/:id/payment" element={<PrivateRoute><PaymentConfirmation /></PrivateRoute>} />
                <Route path="/invoice/:id/cancel" element={<PrivateRoute><InvoiceCancel /></PrivateRoute>} />
                <Route path="/welcome/:token/*" element={<WelcomeInvitation />} />
                <Route path="/accept-invitation/:token/*" element={<WelcomeInvitation />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Router>
        </ThemeProvider>
      </ProfileProvider>
    </ErrorBoundary>
  );
}

export default App;
